import {
  computed,
  inject,
  Injectable,
  Injector,
  linkedSignal,
  resource,
  Signal,
  signal,
} from '@angular/core';
import {
  MbscCellClickEvent,
  MbscEventClickEvent,
  MbscEventCreateEvent,
  MbscEventDeleteEvent,
  MbscEventDragEvent,
  MbscResource,
} from '@mobiscroll/angular';
import { block, handleRequestError, MenuItem } from '@softline/application';
import { DateTimeService, toLocaleISOString } from '@softline/core';
import { askDeleteFn, MessageBarService, modalFn } from '@softline/ui-core';
import { RosterContentCreateComponent } from '../modals/roster-content-create/roster-content-create.component';
import { RosterContentEditComponent } from '../modals/roster-content-edit/roster-content-edit.component';
import { RosterEventRepository } from '../repositories/event/roster-event.repository';
import {
  LoadRosterDetailParams,
  RosterResourceRepository,
} from '../repositories/resource/roster-resource.repository';
import { RosterStructureRepository } from '../repositories/structure/roster-structure.repository';
import { SOFTLINE_ROSTER_TIME_CONFIG } from '../roster.shared';
import { AdditionalData } from '../types/additional-data';
import { CalendarViewType } from '../types/calendar-view-type';
import { Department } from '../types/department';
import { DepartmentHead } from '../types/department-head';
import { DetailEmployee, Employee } from '../types/employee';
import { PmsCalendarResource } from '../types/pms-calendar-resource';
import { Task } from '../types/pms-task';
import { RosterContent } from '../types/roster-content';
import { Structure } from '../types/structure';
import { RosterService } from './roster.service';

@Injectable()
export class PmsService extends RosterService<RosterContent> {
  override calendarDetails: Signal<'compact' | 'all'> = signal<'compact' | 'all'>(
    'compact'
  );
  private readonly modal = modalFn();
  private readonly askDelete = askDeleteFn();
  private readonly timeConfig = inject(SOFTLINE_ROSTER_TIME_CONFIG, {
    optional: true,
  }) ?? {
    startHour: 6,
    endHour: 20,
  };
  override handleContextMenuAction(action: string): void {
    console.log('Context menu action received in service:', action);
  }
  readonly #messageBar = inject(MessageBarService);
  readonly injector = inject(Injector);
  //readonly #locale = inject(LOCALE_ID);

  override refreshEvents(): void {
    console.log('Refreshing events...');
  }

  readonly #structureRepository = inject<RosterStructureRepository<Structure>>(
    RosterStructureRepository
  );

  readonly #eventRepository: RosterEventRepository<RosterContent> = inject(
    RosterEventRepository<RosterContent>
  );

  readonly #resourceRepository: RosterResourceRepository<PmsCalendarResource, any> =
    inject(RosterResourceRepository<PmsCalendarResource, any>);

  readonly #begindateSource = signal<Signal<string | null>>(
    signal(inject(DateTimeService).now())
  );

  override readonly startOfPeriod: any;
  override readonly endOfPeriod: any;

  readonly #selectedEventIds = signal<Set<string | number>>(new Set());

  override menuItems = computed<MenuItem[]>(() => [
    {
      name: 'pms-roster',
      title: 'Dienstplaner',
      icon: 'fa-regular fa-calendar-days',
      type: 'route',
      outlet: 'nav',
      routerLink: ['/roster/pms'],
    },
  ]);

  readonly #events = resource({
    params: () => {
      const resources = this.resources();
      if (!resources || resources.length < 1) return undefined;
      return this.params();
    },
    loader: async ({ params }) => {
      if (!params) return Promise.resolve(undefined);

      const allEventsMap = new Map<string | number, RosterContent>();
      const children = this.resources().flatMap((o) => o.children ?? []);

      for (const child of children) {
        if (!child.id) continue;
        try {
          const events = await this.#eventRepository.loadEvents(
            params.pathParams.employeeId,
            params.queryParams.begindate,
            params.queryParams.enddate,
            Number(child.id)
          );
          if (!events || events.length < 1) continue;
          events.forEach((event) => {
            if (!event.id) return;
            allEventsMap.set(event?.id, event);
          });
        } catch (e) {
          handleRequestError(e, this.injector, { show: true });
        }
      }
      return allEventsMap;
    },
  });

  readonly #structure = resource({
    params: () => this.params(),
    loader: async ({ params }) => {
      if (!params) return Promise.resolve(undefined);

      return this.#structureRepository.load({
        employeeId: params.pathParams.employeeId,
        begindate: params.queryParams.begindate,
        enddate: params.queryParams.enddate,
      });
    },
  });

  readonly begindate = linkedSignal(() => this.#begindateSource()());
  readonly enddate = signal<string>(inject(DateTimeService).now());
  readonly selectedDepartmentHead = signal<Signal<DepartmentHead | null>>(signal(null));
  readonly employeeDetail = signal<Map<number | string, DetailEmployee> | null>(null);
  readonly selectedEventIds = computed(() => Array.from(this.#selectedEventIds()));
  readonly structure = computed(() => this.#structure.value());
  readonly events = computed(() =>
    Array.from(this.#events.value()?.values() ?? []).filter((o) => o !== undefined)
  );

  override registerEventTypesFilter(value: Signal<string[]>): void {
    console.log(value);
  }

  private readonly map = new Map<number | string, DetailEmployee>();

  readonly params = computed(() => {
    const date = this.begindate();
    const departmentHead = this.selectedDepartmentHead()();

    if (!departmentHead || !date) return undefined;

    return {
      pathParams: {
        employeeId: departmentHead.employee.id,
      },
      queryParams: {
        begindate: date, //formatRequestDate(date, this.#locale),
        enddate: date, //formatRequestDate(date, this.#locale),
      },
    };
  });

  readonly resources: Signal<MbscResource[]> = computed(() => {
    const structure = this.#structure.value();
    if (!structure) return [];

    return structure.departements.map((departement) => {
      return {
        id: departement.department.id,
        name: departement.department.name,
        key: departement.department.key,
        children: departement.employees.map((employee) => {
          return {
            id: employee.id,
            name: employee.name,
            key: employee.key,
          };
        }),
      };
    });
  });

  override registerDepartmentHead(value: Signal<DepartmentHead | null>) {
    this.selectedDepartmentHead.set(value);
  }

  override registerBeginDate(value: Signal<string>) {
    this.#begindateSource.set(value);
  }

  override registerCalenderViewType(value: Signal<CalendarViewType>): void {
    console.log(value);
  }

  override registerPlanMonthRange(): void { /* not used */ }

  override registerCalendarColumnWidth(value: Signal<string>): void {
    console.log(value);
  }

  override registerCalendarDetails(value: Signal<'compact' | 'all'>): void {
    console.log(value);
  }

  override registerResourceFilterString(value: Signal<string>): void {
    console.log(value);
  }

  override async onEventCreate(e: MbscEventCreateEvent, inst: any): Promise<void> {
    if (e.action === 'externalDrop') {
      const start = toLocaleISOString(e.event.start?.toString() ?? '');
      const end = toLocaleISOString(e.event.end?.toString() ?? '');
      const structure = this.#structure.value();

      let department: Department | null = null;

      structure?.departements.forEach((d) => {
        if (d.employees.findIndex((emp) => emp.id === e.event.resource) >= 0) {
          department = d.department;
        }
      });

      let task;
      // Prüfen ob es Eine Absence ist
      if ('id' in (e.event['task'] ?? {})) {
        task = e.event['task'];
      } else {
        task = {
          id: e.event.id,
          key: e.event['key'],
          numberPerWeekRec: e.event['numberPerWeekRec'],
          numberPerWeekPlanned: e.event['numberPerWeekPlanned'],
          weekday: e.event['weekday'],
          name: e.event['name'],
        } as Task;
      }

      const body = [
        {
          resource: [e.event.resource],
          allDay: e.event.allDay,
          date: start,
          start:
            start.split('T')[0] +
            'T' +
            this.timeConfig.startHour.toString().padStart(2, '0') +
            ':00:00.000',
          end:
            end.split('T')[0] +
            'T' +
            this.timeConfig.endHour?.toString().padStart(2, '0') +
            ':00:00.000',
          title: e.event['label'],
          additionalData: {
            employees: [
              {
                department: department,
                employee: {
                  id: e.event.resource,
                },
              },
            ],
            begintime:
              start.split('T')[0] +
              'T' +
              this.timeConfig.startHour.toString().padStart(2, '0') +
              ':00:00.000',
            endtime:
              end.split('T')[0] +
              'T' +
              this.timeConfig.endHour?.toString().padStart(2, '0') +
              ':00:00.000',
            type: e.event['type'] ?? 'PLAN',
            status: e.event['status'] ?? 'U',
            pauseHours: '00:30:00', //TODO:
            plannedHours: '23:59:59', //TODO:
            targetHours: '23:59:59', // TODO:
            task: task,
            department: department,
            additionaldetail: [],
          },
        },
      ] as Partial<RosterContent>;

      const result = await this.#eventRepository.createEvent(body);
      const eventMap = this.#events.value();
      if (!result.id || !eventMap) return;

      this.#events.value.set(new Map(eventMap.set(result.id, result)));
    }
  }

  override async onEventClick(args: MbscEventClickEvent, inst: any): Promise<void> {
    this.toggleSelectedEvents(args.event as RosterContent);
  }

  override async onEventDoubleClick(e: MbscEventClickEvent, inst: any): Promise<void> {
    const departmentHead = this.selectedDepartmentHead()();
    if (!departmentHead) return;
    const detail = await this.#eventRepository.loadEventDetail(
      departmentHead.employee.id,
      e.event.id as unknown as number
    );
    if (!detail) return;
    await this.modal({
      component: RosterContentEditComponent,
      title: 'Tätigkeit Bearbeiten',
      data: {
        event: e.event as RosterContent,
        additionalData: detail as AdditionalData,
        onSave: async (task) => {
          let department: Department | null = null;
          const structure = this.#structure.value();

          structure?.departements.forEach((d) => {
            if (d.employees.findIndex((emp) => emp.id === e.event.resource?.[0]) >= 0) {
              department = d.department;
            }
          });

          // Backend-only response fields must not be sent back
          const additionalData = { ...task.additionalData };
          delete additionalData.billable;
          delete additionalData.creatorUser;
          delete additionalData.createdAt;
          const body = {
            ...task,
            additionalData: {
              ...additionalData,
              employees: [
                {
                  department: department,
                  employee: {
                    id: e.event.resource?.[0],
                  },
                },
              ],
              department: department,
            },
          } as any as RosterContent;
          await block(async () => {
            try {
              await this.#eventRepository.updateEvent(body);
              this.#messageBar.success('Tätigkeit wurde erfolgreich bearbeitet.');
            } catch (e) {
              handleRequestError(e, this.injector, { show: true });
            }
          }, this.injector);
        },
      },
    });
  }

  override async onCellClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    this.#selectedEventIds.set(new Set());
  }

  override async onCellDoubleClick(args: MbscCellClickEvent, inst: any): Promise<void> {
    await this.modal({
      component: RosterContentCreateComponent,
      title: 'Tätigkeit Erstellen',
      data: {
        date: args.date.toISOString(),
        resource: args.resource as number,
        onSave: async (body) => {
          await block(async () => {
            try {
              await this.#eventRepository.createEvent(body);
              this.#messageBar.success('Tätigkeit wurde erfolgreich erstellt.');
            } catch (e) {
              handleRequestError(e, this.injector, { show: true });
            }
          }, this.injector);
        },
      },
    });
  }

  // wird auch bei external Drag aufgerufen
  override async onEventDragStart(args: MbscEventDragEvent, inst: any) {
    console.log('PMSSERVICE onEventDragStart');
  }

  // wird auch bei external Drop in Calendar aufgerufen
  override async onEventDragEnd(args: MbscEventDragEvent, inst: any) {
    // Prüfung das es kein External Drop ist (External Events haben kein additionalData)
    if (args.event['additionalData']) {
      await block(async () => {
        try {
          // Backend-only response fields must not be sent back
          const additionalData = { ...args.event['additionalData'] };
          delete additionalData.billable;
          delete additionalData.creatorUser;
          delete additionalData.createdAt;
          await this.#eventRepository.updateEvent({
            ...args.event,
            additionalData: {
              ...additionalData,
              type: 'PLAN',
              additionaldetail: (additionalData.additionaldetail ?? [{}]).map(
                (detail: any) => {
                  const cleaned = { ...detail };
                  delete cleaned.hoursPlannedArtefact;
                  delete cleaned.hoursActualArtefact;
                  return cleaned;
                }
              ),
            },
          } as RosterContent);
          this.#messageBar.success('Tätigkeit wurde erfolgreich verschoben');
        } catch (e) {
          handleRequestError(e, this.injector, { show: true });
        }
      }, this.injector);
    }
  }
  //TODO:
  override onEventDelete(args: MbscEventDeleteEvent, inst: any): boolean {
    const event = args.event as RosterContent;
    if (!event) return false;
    this.deleteEvents([event]);
    return true;
  }

  override async deleteSelectedEvents(): Promise<boolean> {
    const selectedEvents = this.#selectedEventIds();
    const eventMap = this.#events.value();
    if (selectedEvents.size < 1 || !eventMap) return false;
    await this.deleteEvents(this.getEventsFromSelection(selectedEvents));
    return true;
  }

  override async deleteEvents(events: RosterContent[]): Promise<boolean> {
    if (events.length < 0 || !events) return Promise.resolve(false);

    try {
      const result = await this.askDelete({
        dismiss: true,
        question: 'Möchten Sie diese Tätigkeiten löschen?',
      });

      if (!result || result === 'DISMISSED' || result === 'CANCEL' || result === 'NO')
        return Promise.resolve(false);

      await this.#eventRepository.deleteEvents(
        events.map((e) => {
          // Backend-only response fields must not be sent back
          const additionalData = { ...e.additionalData };
          delete additionalData.billable;
          delete additionalData.creatorUser;
          delete additionalData.createdAt;
          const detail = { ...(additionalData.additionaldetail[0] ?? {}) };
          delete detail.hoursPlannedArtefact;
          delete detail.hoursActualArtefact;
          return {
            ...e,
            additionalData: {
              ...additionalData,
              additionaldetail: detail,
            },
          };
        }) as any
      );
      this.#messageBar.success('Events wurden erfolgreich entfernt.');
    } catch (e) {
      handleRequestError(e, this.injector, { show: true });
    }

    return Promise.resolve(true);
  }

  override async onEventRightClick(): Promise<void> {
    /* no context menu for the pms roster */
  }

  override async onCellRightClick(): Promise<void> {
    /* no context menu for the pms roster */
  }

  override async onKeyDown(event: KeyboardEvent): Promise<void> {
    const selectedEvents = this.#selectedEventIds();
    const eventMap = this.#events.value();

    if (selectedEvents.size < 1 || !eventMap) return;

    if (event.key === 'Unidentified' || event.key === '') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    /*if (event.key === 'Backspace' || event.key === 'Delete') {
      console.log('Delete key pressed', event);
      event.preventDefault();
      event.stopPropagation();
      await this.deleteEvents(this.getEventsFromSelection(selectedEvents));
    }*/
  }

  private toggleSelectedEvents(event: RosterContent) {
    if (!event.id) return;

    const selection = this.#selectedEventIds();

    if (selection.has(event.id)) {
      selection.delete(event.id);
      this.#selectedEventIds.set(new Set(selection));
    } else {
      const updatedSet = selection.add(event.id);
      this.#selectedEventIds.set(new Set(updatedSet));
    }
  }

  private getEventsFromSelection(eventIds: Set<string | number>): RosterContent[] {
    const eventMap = this.#events.value();

    if (!eventMap) return [];

    return Array.from(eventIds)
      .filter((o) => o !== undefined)
      .map((eventId) => eventMap.get(eventId))
      .filter((o) => o !== undefined);
  }

  async loadEmployeeContent(structure: Structure, date: string): Promise<void> {
    let currentChunk: Employee[] = [];

    for (const depStructure of structure.departements) {
      for (const employee of depStructure.employees) {
        currentChunk.push(employee);

        if (currentChunk.length < 2) continue;

        await Promise.all(
          currentChunk.map((e) => this.loadDetailPMS({ date: date, employeeId: e.id }))
        );
        currentChunk = [];
      }
    }

    if (currentChunk.length > 0) {
      await Promise.all(
        currentChunk.map((e) => this.loadDetailPMS({ date: date, employeeId: e.id }))
      );
    }
  }

  private async loadDetailPMS(params: LoadRosterDetailParams) {
    const detail = await this.#resourceRepository.loadDetail({
      date: params.date,
      employeeId: params.employeeId,
    });

    this.map.set(params.employeeId, detail);

    this.employeeDetail.set(new Map(this.map));
  }
}
